import { FetchBaseQueryArgs } from '@reduxjs/toolkit/dist/query/fetchBaseQuery';
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '../../../types/redux/states/RootState';
import type { BaseQueryFn } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { ThunkExtraArgs } from '../../../types/redux/utils/ThunkExtraArgs';
import { rumFetchBaseQuery } from '@wsb/gasket-advanced-apm-rum/client';
import { RTKQueryExtraOptions } from '../../../types/redux/utils/RTKQueryExtraOptions';
import { showApiGrowl } from '../../thunks/showApiGrowl';
import { getRootDomain } from '../../selectors/getRootDomain';
import { validateAuth } from '../../../utils/validateAuth';

type Args = {
  subDomain: string;
  basePath: string;
} & Omit<FetchBaseQueryArgs, 'baseUrl'>;

// NOTE: This is a debugging function that logs the fetch request and response

// const debugFetch = function () {
//   console.log(arguments);
//   return (
//     fetch
//       // @ts-ignore
//       .apply(this, arguments)
//       // @ts-ignore
//       .then((response: Response, ...others) => {
//         console.log('response', response);
//         return response;
//       })
//       .catch(r => console.log(r))
//   );
// };

export function fetchQuery({ subDomain, basePath, ...config }: Args): BaseQueryFn {
  // eslint-disable-next-line max-statements
  return async (args, api, extraOptions: RTKQueryExtraOptions) => {
    const thunkExtra = api.extra as ThunkExtraArgs;
    const rumClient = thunkExtra?.rumClient?.get();
    const rootDomain = getRootDomain(api.getState() as RootState);
    const baseUrl = `https://${subDomain}.${rootDomain}/${basePath.replace(/^\/+/g, '')}`;
    if (rumClient) {
      try {
        const authResult = await validateAuth(rumClient, rootDomain, api.endpoint);
        if (authResult && authResult.status !== 201) {
          return {
            error: new Error(
              `Manual heartbeat authentication failed with status: ${authResult.status} for ${api.endpoint}`
            )
          };
        }
      } catch (error) {
        return { error };
      }
    }

    const isServerSide = typeof window === 'undefined';
    const queryFn = isServerSide ? fetchBaseQuery : rumFetchBaseQuery;

    const result = await queryFn({
      credentials: 'include',
      ...config,
      baseUrl,
      prepareHeaders: async (headers: Headers, customApi: any) => {
        if (config.prepareHeaders) {
          await config.prepareHeaders(headers, customApi);
        }

        if (!headers.has('Cookie') && !!thunkExtra.serverAuthCookie) {
          headers.set('Cookie', thunkExtra.serverAuthCookie);
        }

        headers.set('X-App-Key', 'marketing-email-dashboard-gum');
      }
    })(args, api, extraOptions);

    if (extraOptions?.growl) {
      api.dispatch(
        showApiGrowl({
          ...extraOptions?.growl,
          apiResultsIsError: !!result.error
        })
      );
    }

    if (result.error && thunkExtra?.serverLogger?.get()) {
      thunkExtra.serverLogger.get().error('API Error:', {
        api: api.endpoint,
        url: args.url,
        payload: result.error
      });

      result.error.data = {
        api: api.endpoint,
        url: `${baseUrl}${args.url}`,
        response: result.error.data
      };
    }

    return result;
  };
}
